import { Receipt } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Invoice } from './types';
import { formatCurrency } from './utils';

interface InvoicePreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  previewInvoices: Invoice[];
  onCreateInvoices: () => void;
}

export function InvoicePreviewDialog({
  open,
  onOpenChange,
  previewInvoices,
  onCreateInvoices
}: InvoicePreviewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Förhandsgranska fakturor ({previewInvoices.length} st)</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lgh</TableHead>
                <TableHead>Boende</TableHead>
                <TableHead className="text-right">Grund</TableHead>
                <TableHead className="text-right">Justeringar</TableHead>
                <TableHead className="text-right">Delade</TableHead>
                <TableHead className="text-right">Totalt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {previewInvoices.map(inv => (
                <TableRow key={inv.id}>
                  <TableCell>{inv.apartment_number}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{inv.resident_name}</p>
                      <p className="text-xs text-slate-500">{inv.resident_email || 'Ingen e-post'}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">{formatCurrency(inv.base_amount)}</TableCell>
                  <TableCell className="text-right">
                    {inv.adjustments.reduce((sum, a) => sum + a.amount, 0) !== 0 && (
                      <span className={inv.adjustments.reduce((sum, a) => sum + a.amount, 0) < 0 ? 'text-green-600' : 'text-orange-600'}>
                        {formatCurrency(inv.adjustments.reduce((sum, a) => sum + a.amount, 0))}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {inv.shared_costs.reduce((sum, c) => sum + c.amount, 0) > 0 && 
                      formatCurrency(inv.shared_costs.reduce((sum, c) => sum + c.amount, 0))
                    }
                  </TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(inv.total_amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex justify-between items-center pt-4 border-t">
            <p className="text-slate-500">
              Summa: {formatCurrency(previewInvoices.reduce((sum, inv) => sum + inv.total_amount, 0))}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={onCreateInvoices} data-testid="create-invoices-btn">
            <Receipt size={16} className="mr-2" />
            Skapa {previewInvoices.length} fakturor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
